import _ from "lodash";

export interface IMasonDocument {
	"@meta"?: MasonMeta;
	"@namespaces"?: MasonNamespaces;
	"@controls"?: MasonControls;
	"@error"?: MasonError;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[x: string]: any;
}

export interface MasonError {
	"@id"?: string;
	"@message": string;
	"@code"?: string;
	"@messages"?: Array<string>;
	"@details"?: string;
	"@httpStatusCode"?: number;
	"@controls"?: MasonControls;
	"@time"?: Date;
}

export interface MasonMeta {
	"@controls"?: MasonControls;
	"@description"?: string;
	"@title"?: string;
}

export interface MasonControls {
	self: MasonControl;
	[key: string]: MasonControl;
}

export interface MasonControl {
	href: string;
	isHrefTemplate?: boolean;
	title?: string;
	description?: string;
	method?: "GET" | "PUT" | "POST" | "DELETE" | "PATCH";
	encoding?: string;
	schema?: Record<string, unknown>;
	schemaUrl?: string;
	template?: Record<string, unknown>;
	accept?: Array<string>;
	output?: Array<string>;
	files?: Array<Record<string, unknown>>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	alt?: Array<any>;
	data?: Record<string, unknown>;
}

export interface MasonNamespaces {
	[key: string]: MasonNamespace;
}

export interface MasonNamespace {
	name: string;
}

class MasonDocument {
	/**
	 * Get a mason @control from a mason document object
	 *
	 * The path variable is optional, it can be used to
	 * find nested mason controls.
	 *
	 * *Example:*
	 *
	 * { forms: [{ id: formA, @controls:{} }, { id: formB }]}
	 *
	 * Controls from formA in the example above could be access using the following path:
	 *
	 * ["forms", "0"]
	 *
	 * @param doc a mason document object containing @controls
	 * @param key the key of the @control to be returned
	 * @param path optional path to the correct context containing the @control
	 * @see getControl
	 */
	static getControl(doc: IMasonDocument, key: string, path?: Array<string>) {
		// Check where in the document we want to get our controls from
		const context = path ? MasonDocument.getContext(doc, path) : doc;

		// Get the controls
		return MasonDocument.getControls(context)?.[key];
	}

	/**
	 * Check if a mason control exists
	 *
	 * @param doc a mason document object containing @controls
	 * @param key the key of the @control to be returned
	 * @param path optional path to the correct context containing the @control
	 * @returns boolean
	 * @see getControl
	 */
	static hasControl(doc: IMasonDocument, key: string, path?: Array<string>) {
		try {
			if (MasonDocument.getControl(doc, key, path)) return true;
			return false;
		} catch (error) {
			return false;
		}
	}

	/**
	 * Get the mason controls from the root of a mason document, or some other context that contains @controls
	 *
	 * @param doc the context from which to get mason controls
	 */
	static getControls(doc: IMasonDocument) {
		return doc["@controls"];
	}

	/**
	 * Takes a path array and tries to follow it and return the context that we're interested in
	 * MasonDocument.will normally be used when trying to find a mason @control nested within somewhere
	 * within the mason object
	 *
	 * @param doc mason document object
	 * @param keys path to the context
	 */
	static getContext(doc: IMasonDocument, keys: Array<string>) {
		return _.get(doc, keys);
	}

	/**
	 * Checks if the context exists within the json doc
	 *
	 * @param doc mason document object
	 * @param keys path to the context
	 */
	static hasContext(doc: IMasonDocument, keys: Array<string>) {
		return MasonDocument.getContext(doc, keys);
	}

	static getControlTemplate(
		doc: IMasonDocument,
		key: string,
		path?: string[]
	) {
		return MasonDocument.getControl(doc, key, path)?.["template"];
	}

	/**
	 * Recursively finds any mason controls within the context and expands them,
	 * returns a new object
	 *
	 * @param context context within a mason document containing controls curied to be expanded
	 * @param namespaces mason namespace objects for resolving curied keys
	 */
	static resolveCuries(context: IMasonDocument, namespaces: MasonNamespaces) {
		const c = _.cloneDeep(context);

		Object.keys(c).forEach(key => {
			if (key === "@controls") {
				const controls = c[key] as MasonControls;
				c[key] = MasonDocument.expandControls(controls, namespaces);
			} else if (typeof c[key] === "object" || Array.isArray(c[key])) {
				MasonDocument.resolveCuries(c[key], namespaces);
			}
		});

		return c;
	}

	/**
	 * Expands curied control keys
	 *
	 * @param key mason control key
	 * @param namespaces mason namespace objects for resolving curied keys
	 */
	static parseCurieControlKey(key: string, namespaces: MasonNamespaces) {
		// Regex pattern for matching our curies
		const split = key.match(/^((\w+)?(?=:):)?(\w+)$/);

		if (!split) return key;

		// No prefix
		if (split[2] === undefined) return split[3];

		// If we have a prefix we grab it from namespaces
		return namespaces[split[2]].name + split[3];
	}

	/**
	 * Parse a set of mason controls expanding any curied keys that are found,
	 * returns a new object
	 *
	 * @param controls an object containing mason controls to be expanded
	 * @param namespaces mason namespace object
	 */
	static expandControls(
		controls: MasonControls,
		namespaces: MasonNamespaces
	) {
		const c = _.cloneDeep(controls);
		Object.keys(c).forEach(key => {
			if (key.indexOf(":") === -1) return;

			c[MasonDocument.parseCurieControlKey(key, namespaces)] = c[key];
			delete c[key];
		});
		return c;
	}

	/**
	 * Takes a mason templated uri and a data object, and returns the URI with values in place
	 *
	 * @param template the uri containing template
	 * @param data data object containing the properties to be added to the uri
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static resolveHrefTemplate(template: string, data: { [k: string]: any }) {
		const uri = new URL(template);
		// const rgx = new RegExp(/\{(w+)\}/, 'g');

		uri.searchParams.forEach((value, name) => {
			const dataValue = data[name] || "";
			uri.searchParams.set(name, dataValue);
		});

		return uri.href;
	}
}

export default MasonDocument;
